import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { MaskLines } from "./Reveal";
import { DONATE_URL } from "./data";
import { SmartLink } from "./SmartLink";
import { scrollToId } from "./Header";

const EASE = [0.22, 1, 0.36, 1];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const photoY = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 80, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 80, damping: 18 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="top" ref={ref} onMouseMove={onMove} data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden bg-[#FAF7F2] pt-28 pb-16">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-0">
        <img src="/images/p5.jpg" alt="" className="w-full h-[120%] object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#FAF7F2]/40 via-[#FAF7F2]/70 to-[#FAF7F2]" />
      </motion.div>
      <div className="grain" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 lg:gap-6 items-center min-h-[calc(100svh-11rem)]">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
            className="font-mono text-xs tracking-[0.3em] uppercase text-[#B34C0A] mb-6"
            data-testid="hero-eyebrow"
          >
            Not-for-profit · Telangana, India
          </motion.p>
          <h1 data-testid="hero-tagline-heading" className="font-serif text-[#0F1D2F] text-[3.4rem] leading-[0.95] sm:text-7xl lg:text-[7.2rem] tracking-[-0.02em]">
            <MaskLines lines={["Ignite", "Awareness,"]} delay={0.25} />
            <MaskLines lines={["Inspire Change."]} delay={0.55} lineClass="italic text-fire" />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1, ease: EASE }}
            className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-[#2B3A4A]"
            data-testid="hero-about-text"
          >
            <strong className="text-[#0F1D2F]">Wings of Fire Foundation</strong> is a not-for-profit organization dedicated to serving underprivileged communities and children with disabilities. We believe that every individual deserves equal opportunities, dignity, and the chance to reach their full potential through education, healthcare, skill development, and social empowerment.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.2, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <SmartLink href={DONATE_URL} data-testid="hero-donate-button" className="btn-fire btn-lg">
              Donate Now <ArrowUpRight className="w-5 h-5" />
            </SmartLink>
            <button onClick={() => scrollToId("#areas")} data-testid="hero-explore-button" className="btn-ghost btn-lg">
              Our work <ArrowDown className="w-4 h-4" />
            </button>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative h-[460px] sm:h-[560px] [perspective:1200px]">
          <motion.div
            style={{ y: photoY, rotateX: rx, rotateY: ry }}
            initial={{ clipPath: "inset(100% 0% 0% 0% round 999px 999px 32px 32px)" }}
            animate={{ clipPath: "inset(0% 0% 0% 0% round 999px 999px 32px 32px)" }}
            transition={{ duration: 1.4, delay: 0.4, ease: EASE }}
            className="absolute inset-x-6 sm:inset-x-10 top-0 bottom-10 overflow-hidden shadow-[0_40px_80px_-30px_rgba(179,76,10,0.5)] [transform-style:preserve-3d]"
            data-testid="hero-photo-arch"
          >
            <img src="/images/p12.jpg" alt="Wings of Fire Foundation community gathering" className="w-full h-full object-cover scale-110" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F1D2F]/60 via-transparent to-transparent" />
            <p className="absolute bottom-6 right-6 text-right text-white font-serif italic text-2xl">Together, we rise.</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.6, rotate: -12 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1.2, delay: 1.1, ease: EASE }}
            className="absolute -left-2 sm:-left-6 bottom-0 w-36 h-36 sm:w-44 sm:h-44 rounded-full bg-[#0F1D2F] flex items-center justify-center shadow-2xl"
            data-testid="hero-badge"
          >
            <svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full spin-slow">
              <defs><path id="circ" d="M100,100 m-78,0 a78,78 0 1,1 156,0 a78,78 0 1,1 -156,0" /></defs>
              <text fill="#F2B36B" fontSize="13" letterSpacing="4" fontFamily="JetBrains Mono, monospace">
                <textPath href="#circ">IGNITE AWARENESS · INSPIRE CHANGE · </textPath>
              </text>
            </svg>
            <img src="/images/wings.png" alt="" className="w-20 sm:w-24 relative" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
